import { useState } from 'react';
import { api } from '../api';
import { t } from '../theme';

/**
 * Two steps: prove the credentials, then choose which list.
 *
 * Nothing is stored until both are done — there is no point persisting a
 * password before knowing it works. A real form rather than sequential
 * prompts, so a password manager can fill it.
 */
export function BringConnect({ onClose, onConnected }: { onClose: () => void; onConnected: () => void }) {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [lists, setLists] = useState<Array<{ listUuid: string; name: string }> | null>(null);
  const [chosen, setChosen] = useState<string | null>(null);
  const [migrate, setMigrate] = useState(true);
  const [remember, setRemember] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const loadLists = async () => {
    if (busy) return;
    setBusy(true);
    setError(null);
    try {
      const res = await api.bringLists(email.trim(), password);
      setLists(res.lists);
      setChosen(res.defaultListUuid || res.lists[0]?.listUuid || null);
      if (res.lists.length === 0) setError('Dieser Bring-Account hat keine Listen');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Anmeldung fehlgeschlagen');
    } finally {
      setBusy(false);
    }
  };

  const connect = async () => {
    const list = lists?.find((l) => l.listUuid === chosen);
    if (!list || busy) return;
    setBusy(true);
    setError(null);
    try {
      await api.bringConnect({
        email: email.trim(),
        password,
        listUuid: list.listUuid,
        listName: list.name,
        migrateExisting: migrate,
        rememberPassword: remember,
      });
      onConnected();
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Verbinden fehlgeschlagen');
    } finally {
      setBusy(false);
    }
  };

  const checkbox = (
    value: boolean,
    set: (v: boolean) => void,
    label: string,
    help?: string,
  ) => (
    <div
      className="press"
      onClick={() => set(!value)}
      style={{ display: 'flex', alignItems: 'flex-start', gap: 12, fontSize: 17, color: t.inkMuted }}
    >
      <div
        style={{
          width: 26,
          height: 26,
          flex: 'none',
          marginTop: 2,
          borderRadius: 8,
          background: value ? t.accent : 'transparent',
          border: value ? 'none' : '1px solid oklch(1 0 0 / 0.2)',
          color: t.accentInk,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          fontSize: 16,
        }}
      >
        {value ? '✓' : ''}
      </div>
      <div style={{ minWidth: 0 }}>
        <div>{label}</div>
        {help && <div style={{ fontSize: 14, color: t.inkFaint, marginTop: 3, textWrap: 'pretty' }}>{help}</div>}
      </div>
    </div>
  );

  const field = (label: string, value: string, onChange: (v: string) => void, type: string, autoComplete: string) => (
    <label style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
      <span style={{ fontFamily: t.mono, fontSize: 12, letterSpacing: '0.14em', color: t.inkFaint }}>{label}</span>
      <input
        value={value}
        onChange={(e) => onChange(e.target.value)}
        onKeyDown={(e) => e.key === 'Enter' && void loadLists()}
        type={type}
        autoComplete={autoComplete}
        autoCapitalize="none"
        autoCorrect="off"
        spellCheck={false}
        style={{
          background: 'oklch(0.2 0.012 60)',
          border: '1px solid oklch(1 0 0 / 0.12)',
          borderRadius: 16,
          padding: '16px 18px',
          color: t.ink,
          caretColor: t.accent,
          fontSize: 22,
          fontFamily: t.sans,
          outline: 'none',
        }}
      />
    </label>
  );

  return (
    <div
      onClick={onClose}
      style={{
        position: 'absolute',
        inset: 0,
        background: 'oklch(0.12 0.01 60 / 0.72)',
        backdropFilter: 'blur(8px)',
        animation: 'fade 160ms ease-out',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 35,
      }}
    >
      <form
        onClick={(e) => e.stopPropagation()}
        onSubmit={(e) => {
          e.preventDefault();
          if (lists) void connect();
          else void loadLists();
        }}
        style={{
          width: 620,
          maxWidth: '92vw',
          borderRadius: 30,
          background: t.surface,
          border: '1px solid oklch(1 0 0 / 0.09)',
          padding: 28,
          display: 'flex',
          flexDirection: 'column',
          gap: 16,
          animation: 'sheet 200ms ease-out',
        }}
      >
        <div style={{ fontFamily: t.mono, fontSize: 12, letterSpacing: '0.14em', color: t.inkFaint }}>
          BRING! VERBINDEN
        </div>
        <div style={{ fontSize: 30, fontWeight: 500, letterSpacing: '-0.035em' }}>
          {lists ? 'Welche Liste?' : 'Bei Bring anmelden'}
        </div>

        {!lists && (
          <>
            {field('E-MAIL', email, setEmail, 'email', 'username')}
            {field('PASSWORT', password, setPassword, 'password', 'current-password')}
            <div style={{ fontSize: 15, color: t.inkMuted, textWrap: 'pretty' }}>
              Bring hat keine offizielle Schnittstelle und bietet kein OAuth für fremde Apps —
              frisk meldet sich wie die Web-App an. Wenn dein Bring-Konto über Google oder Apple
              angelegt wurde, hast du eventuell gar kein Passwort und musst erst eins setzen.
            </div>
          </>
        )}

        {lists && (
          <>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8, maxHeight: 260, overflowY: 'auto' }}>
              {lists.map((l) => (
                <div
                  key={l.listUuid}
                  className="press"
                  onClick={() => setChosen(l.listUuid)}
                  style={{
                    padding: '16px 18px',
                    borderRadius: 16,
                    background: chosen === l.listUuid ? 'oklch(0.31 0.012 60)' : 'oklch(0.26 0.012 60)',
                    border: `1px solid ${chosen === l.listUuid ? t.accent : 'oklch(1 0 0 / 0.06)'}`,
                    fontSize: 21,
                    fontWeight: 500,
                  }}
                >
                  {l.name}
                </div>
              ))}
            </div>
            {checkbox(migrate, setMigrate, 'Bisherige frisk-Einträge zu Bring übertragen')}
            {checkbox(
              remember,
              setRemember,
              'Passwort speichern, damit die Verbindung sich selbst erneuern kann',
              'Ohne das speichert frisk nur den Refresh-Token. Der reicht im Alltag — läuft er irgendwann ab, verbindest du einmal neu.',
            )}
          </>
        )}

        {error && <div style={{ fontSize: 17, color: 'oklch(0.82 0.14 30)' }}>{error}</div>}

        <div style={{ display: 'flex', gap: 12, marginTop: 4 }}>
          <div
            className="press"
            onClick={onClose}
            style={{ height: 68, padding: '0 26px', borderRadius: 18, border: t.hairlineStrong, display: 'flex', alignItems: 'center', fontSize: 20, fontWeight: 500, color: 'oklch(0.8 0.015 70)' }}
          >
            Abbrechen
          </div>
          <div style={{ flex: 1 }} />
          <button
            type="submit"
            disabled={busy || (lists ? !chosen : !email.trim() || !password)}
            style={{
              height: 68,
              padding: '0 34px',
              borderRadius: 18,
              border: 'none',
              background: t.accent,
              color: t.accentInk,
              fontSize: 21,
              fontWeight: 600,
              fontFamily: t.sans,
              cursor: busy ? 'wait' : 'pointer',
              opacity: busy || (lists ? !chosen : !email.trim() || !password) ? 0.4 : 1,
            }}
          >
            {busy ? 'Moment …' : lists ? 'Verbinden' : 'Weiter'}
          </button>
        </div>
      </form>
    </div>
  );
}
